import { Component, inject, OnInit, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { AppInformationService, AuthService, GenesisUnitOfWork, InvoiceService, ReportService, SessionInformationService, UtilityService } from 'app/core/core.module';
import { Guid } from 'app/core/data/entities';
import { Ach, AchNote, ConfirmationPaymentMethod, ConfirmationType, Invoice, Order } from 'app/core/model/entity-model';
import { EmailDialogComponent } from 'app/shared/email-dialog/email-dialog.component';
import { InputDialogComponent } from 'app/shared/input-dialog/input-dialog.component';
import { SharedModule } from 'app/shared/shared.module';
import { DxDataGridComponent } from 'devextreme-angular';
import { alert, confirm } from 'devextreme/ui/dialog';
import _ from 'lodash';
import { BsModalService } from 'ngx-bootstrap/modal';
import { Utility } from 'app/core/utility';
import moment from 'moment';

@Component({
    selector: 'app-ach-overview',
    templateUrl: './ach-overview.component.html',
    styleUrls: ['./ach-overview.component.scss'],
    standalone: false,
})
export class AchOverviewComponent implements OnInit {
    uow = inject(GenesisUnitOfWork);
    auth = inject(AuthService);
    router = inject(Router);
    appInfo = inject(AppInformationService);
    reportService = inject(ReportService);
    sessionInfo = inject(SessionInformationService);
    modalService = inject(BsModalService);
    utility = inject(UtilityService);
    invoiceService = inject(InvoiceService);

    aches: Ach[];
    confirmationTypes: ConfirmationType[];
    confirmationPaymentMethods: ConfirmationPaymentMethod[];
    showAll: boolean;

    @ViewChild('dataGrid') dataGrid: DxDataGridComponent;
    onExporting = SharedModule.onExporting;

    async ngOnInit() {
        const showAll = this.sessionInfo.getItem('ach-overview-filter');
        if (showAll != null) this.showAll = showAll;

        this.confirmationTypes = this.uow.lookups.confirmationTypes;
        this.confirmationPaymentMethods = (await this.uow.confirmationPaymentMethods.whereActive()) as ConfirmationPaymentMethod[];
        await this.getData();
    }

    async getData() {
        const aches = await this.uow.getAches('confirmation.order, dealer, achTransactions.invoice.invoiceLines.orderLineDetail.orderLine.order', !this.showAll);
        const achNotes = await this.uow.getAchNotes();
        const detailOverview = await this.uow.getAchDetailStatusOverview();
        aches.forEach((ach: any) => {
            const detail = detailOverview.find((d) => d.achId === ach.id);
            ach.detailsNotComplete = detail && detail.detailsCount > 0 ? detail.detailsNotComplete : true;
            ach.hasInitiatedTransfers = ach.achTransactions.length > 0 && ach.achTransactions.some((tx) => tx.transferInitiated && !tx.depositAcknowledged);
            ach.initiatedTransfers = _.uniq(ach.achTransactions.filter((tx) => tx.transferInitiated).map((tx) => moment(tx.transferInitiated).format('MM/DD/YYYY'))).join(', ');
            ach.readyToInitiate = ach.confirmation?.order?.statusId === Order.Statuses.InProcess;
            ach.readyToComplete =
                ach.achTransactions.length > 0 &&
                Utility.round(
                    ach.achTransactions.filter((tx) => !tx.deleted && !!tx.depositAcknowledged).reduce((sum, tx) => sum + tx.amount, 0),
                    2,
                ) === ach.amount;

            ach.noteCount = achNotes.filter((x) => x.achId == ach.id).length;
            ach.paymentMethodDisplay = ach.confirmation?.paymentMethod || ach.confirmation?.order?.paymentMethod || this.getInvoiceOrderPaymentMethod(ach);
        });
        this.aches = aches;
        this.dataGrid?.instance.clearSelection();
    }

    getInvoiceOrderPaymentMethod(ach: Ach): string {
        for (const tx of ach.achTransactions ?? []) {
            for (const line of (tx.invoice as any)?.invoiceLines ?? []) {
                const paymentMethod = line.orderLineDetail?.orderLine?.order?.paymentMethod;
                if (paymentMethod) return paymentMethod;
            }
        }
        return null;
    }

    async toggleShowAll() {
        this.showAll = !this.showAll;
        await this.getData();
        this.sessionInfo.setItem('ach-overview-filter', this.showAll);
    }

    // this method is used to clear the selection when the user searches for something
    onOptionChanged(e) {
        if (e.fullName === 'searchPanel.text') {
            e.component.__searchChanged = true;
        }
        const filterCheck = _.includes(e.fullName, 'filterValue');
        if (filterCheck) {
            this.dataGrid.instance.clearSelection();
        }
    }

    // this method is used to clear the selection when the user searches for something
    onContentReady(e) {
        if (e.component.__searchChanged) {
            //__searchChanged is for the search box
            e.component.__searchChanged = false;
            this.dataGrid.instance.clearSelection();
        }
    }

    async rowPrepare(e) {
        if (e.rowType !== 'data') return;

        const ach = e.data;
        // console.log(ach);
        if (ach.confirmation?.declined) {
            e.rowElement.className = e.rowElement.className + ' declined-to-acknowledge';
        } else if (!ach.approved) {
            e.rowElement.className = e.rowElement.className + ' waiting-on-dealer-ach';
        } else if (!!ach.approved && !ach.hasInitiatedTransfers && !ach.readyToInitiate && !ach.readyToComplete && !ach.completed) {
            e.rowElement.className = e.rowElement.className + ' ach-approved';
        } else if (ach.readyToInitiate && !ach.hasInitiatedTransfers) {
            e.rowElement.className = e.rowElement.className + ' ach-ready-to-initiate';
        } else if (ach.hasInitiatedTransfers) {
            e.rowElement.className = e.rowElement.className + ' ach-initiated';
        } else if (ach.approved && ach.readyToComplete && !ach.completed) {
            e.rowElement.className = e.rowElement.className + ' ach-ready-to-complete';
        } else if (!!ach.completed) {
            e.rowElement.className = e.rowElement.className + ' ach-complete';
        }
    }

    onToolbarPreparing(e) {
        e.toolbarOptions.items.unshift(
            {
                location: 'before',
                template: 'toolbarButtons',
            },
            {
                template: 'toolbarText',
                location: 'after',
            },
            {
                widget: 'dxButton',
                options: {
                    icon: 'revert',
                    elementAttr: { title: 'Reset Filters' },
                    onClick: () => {
                        e.component.clearFilter();
                    },
                },
                location: 'after',
            },
            {
                widget: 'dxButton',
                options: {
                    icon: 'refresh',
                    elementAttr: { title: 'Reset Grid Layout' },
                    onClick: () => {
                        e.component.state({});
                    },
                },
                location: 'after',
            },
        );
    }

    async rowDoubleClick(e) {
        if (!e.data) return;
        this.open(e.data);
    }

    async open(ach: Ach) {
        if (!ach) return;
        this.router.navigate([`/payments/ach/${ach.id}`]);
    }

    async openOrder(orderId: number) {
        if (!orderId) return;
        this.router.navigate([`/sales-orders/${orderId}`]);
    }

    async openInvoice(e) {
        const invoice = (await this.uow.getInvoiceByInvoiceNumber(e)) as Invoice;
        this.invoiceService.openInvoiceDialog(invoice);
    }

    async viewConfirmation(code: Guid) {
        if (!code) return;
        this.router.navigate([`/portal/dealer-confirmation/${code}`]);
    }

    async viewAchForm(code: Guid) {
        if (!code) return;
        this.router.navigate([`/portal/dealer-ach/${code}`]);
    }

    async delete(ach: Ach) {
        if (!ach) return;

        const result = await confirm(`Are you sure you want to delete the selected ACH?`, this.appInfo.settings.company);
        if (!result) return;

        await this.appInfo.pleaseWaitShow();
        ach.achTransactions.forEach((tx) => {
            tx.deleted = new Date();
            tx.deletedBy = this.auth.currentUser.userName;
        });
        ach.deleted = new Date();
        ach.deletedBy = this.auth.currentUser.userName;
        await this.uow.commit();
        await this.getData();
        this.dataGrid.instance.clearSelection();
        await this.appInfo.pleaseWaitHide();
    }

    async emailAchLink(ach: Ach) {
        if (!ach) return;

        const modalRef = this.modalService.show(EmailDialogComponent, {
            class: 'modal-md',
            ignoreBackdropClick: true,
            keyboard: false,
            initialState: {
                mode: 'AchLink',
                entityId: ach.id,
                dealerEmail: ach.dealer.aPEmail ?? ach.dealer.email,
                body: `Please click the link below to complete the ACH Authorization Form for ${ach.dealer.companyName}.\n\n${this.appInfo.achLinkBaseUrl}${ach.code}`,
            },
        });

        modalRef.content.result.subscribe(async (result) => {
            if (result.success && !!result.value) {
                this.appInfo.pleaseWaitShow();
                console.log(result);
                const emailSent = result.attachInvoices ? await this.utility.sendAchLinkEmailWithInvoices(result.value, ach.id) : await this.utility.sendEmail(result.value);
                await this.uow.commit();
                this.appInfo.pleaseWaitHide();
                await alert(emailSent ? 'Email Sent!' : 'An error occurred while sending the email.', this.appInfo.settings.company);
            }
            modalRef.hide();
        });
    }

    print(ach: Ach) {
        if (!ach) return;
        this.reportService.printAchAuthFormReport(ach.code);
    }

    async completeAch(ach: Ach) {
        if (!ach) return;
        const result = await confirm(`Are you sure you want to mark this ACH as complete?`, this.appInfo.settings.company);
        if (!result) return;

        if (!ach.achTransactions || ach.achTransactions.length === 0) {
            await alert('No ACH transactions found.', this.appInfo.settings.company);
            return;
        }

        const hasPendingTransactions = ach.achTransactions.some((tx) => !tx.deleted && !tx.depositAcknowledged);
        if (hasPendingTransactions) {
            await alert('All transactions must be deposited before completing the ACH.', this.appInfo.settings.company);
            return;
        }

        await this.appInfo.pleaseWaitShow();
        console.log(ach);
        ach.completed = new Date();
        ach.completedBy = this.auth.currentUser.userName;

        await this.uow.commit();
        await this.getData();
        this.dataGrid.instance.clearSelection();
        await this.appInfo.pleaseWaitHide();
    }

    async addNote(ach: Ach) {
        if (!ach) return;

        const modalRef = this.modalService.show(InputDialogComponent, {
            class: 'modal-sm',
            ignoreBackdropClick: true,
            keyboard: false,
            initialState: {
                dialogHeader: 'ACH Note',
                dialogPrompt: 'Enter Note',
                checkNumeric: false,
                checkInteger: false,
                mode: 'textarea',
            },
        });

        modalRef.content.result.subscribe(async (result) => {
            if (result.result && !!result.value) {
                this.appInfo.pleaseWaitShow();
                this.uow.achNotes.createEntity({
                    created: new Date(),
                    createdBy: this.auth.currentUser.userName,
                    note: result.value,
                    achId: ach.id,
                }) as AchNote;
                await this.uow.commit();
                await this.getData();
                this.appInfo.pleaseWaitHide();
            }
            modalRef.hide();
        });
    }
}
